import { number } from '@frameio/i18n';
import { DotSeparator } from '@frameio/shared/components/DotSeparator';
import { useFormattedRelativeDate } from '@frameio/utils';
import { Text } from '@frameio/vapor';
import type { TransferBatch } from '../../../types/uploader';
import {
  getTransferBatchHasLocalData,
  hasPartialFailure,
} from '../../../utils';
import { JobSize } from '../JobSize';
import { ScrollingJob } from '../ScrollingJob';
import TEXT from './generated/TEXT/TranscodeCompleteJobStatus';

TEXT.DEFINE(
  {
    /** Status label when download is complete */
    downloaded: 'Downloaded',
    /** Status label when upload is complete */
    uploaded: 'Uploaded',
    /** Status showing partial upload progress */
    partialUploaded: (uploaded: number, total: number): string =>
      `Uploaded ${number(uploaded)} out of ${number(total)}`,
  },
  { number }
);

export const TranscodeCompleteJobStatus = ({ job }: { job: TransferBatch }) => {
  const {
    completedAt,
    fileCounts,
    batchType,
    progress,
    topLevelFileCount,
    topLevelFolderCount,
    status,
  } = job;

  const isTopLevelFolderOnly =
    (topLevelFolderCount ?? 0) > 0 && topLevelFileCount === 0;

  const hasLocalData = getTransferBatchHasLocalData(job);
  const hasDownloaded = batchType === 'DOWNLOAD';
  const hasUploaded = status === 'SUCCEEDED' && batchType === 'UPLOAD';

  const canShowJobSize =
    hasLocalData &&
    progress?.bytesSent === progress?.bytesTotal &&
    !isTopLevelFolderOnly;

  const partialFailed = hasPartialFailure(job);

  return (
    <>
      <ScrollingJob>
        <>
          <Text color="tertiary" variant="secondary">
            {hasDownloaded && TEXT.downloaded}
            {hasUploaded && !partialFailed && TEXT.uploaded}
            {partialFailed &&
              TEXT.partialUploaded(
                fileCounts?.uploaded ?? 0,
                fileCounts?.total ?? 0
              )}
          </Text>

          <>
            {completedAt && (
              <>
                <DotSeparator />
                <CompletedAtSpan date={new Date(completedAt)} />
              </>
            )}
            {canShowJobSize && (
              <>
                <DotSeparator />
                <JobSize bytesTotal={progress?.bytesTotal} />
              </>
            )}
          </>
        </>
      </ScrollingJob>
    </>
  );
};

const CompletedAtSpan = ({ date }: { date: Date }) => (
  <span className="completed-at">
    {useFormattedRelativeDate({ date, truncate: true, showSuffix: true })}
  </span>
);
